import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs'
// Bundled worker (Vite ?url asset), legacy build for the es2015 target.
// Replaces the copied public/pdf.worker.min.js the predev/prebuild hooks
// used to stage. Same pattern as physician-ui-app.
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'
import ParseoLogo, { ParseoSpinner } from '../shared/ui/ParseoLogo'
import { fitFontSize, fitTextareaFontSize, wrapsInSentPdf } from '../shared/lib/textFitUtils'
import { inferInputFormat } from '../shared/lib/inputFormats'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker

// Loose field shape for the patient viewer. Distinct from the shared FormField
// because this component compares field.type against legacy numeric tokens
// (=== 2, === 5), which the shared string-typed FormField.type would reject.
interface FormFieldLike {
  name?: string
  displayName?: string
  original_name?: string
  type?: string | number
  value?: unknown
  bbox?: number[]
  page?: number
  id?: string
  linkId?: string
  [key: string]: unknown
}

/**
 * PatientFormViewer — PDF-based form viewer for patients.
 * Shows the full form with field overlays. Only fields marked as patient-required
 * (by the physician) and patient signature fields are editable.
 * Patient must fill all required fields + sign before submitting.
 */
export default function PatientFormViewer() {
  const { formId } = useParams()
  const [searchParams] = useSearchParams()
  const assignmentId = searchParams.get('assignment')
  const navigate = useNavigate()

  // PDF state
  const [pdfDoc, setPdfDoc] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [scale, setScale] = useState(1.0)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [rendering, setRendering] = useState(false)

  // Fields state
  const [fields, setFields] = useState<FormFieldLike[]>([])
  const [fieldValues, setFieldValues] = useState<Record<number, string>>({})
  const [requiredFieldIndices, setRequiredFieldIndices] = useState<Set<number>>(new Set())
  const [focusedField, setFocusedField] = useState<number | null>(null)
  const [hoveredField, setHoveredField] = useState<number | null>(null)

  // Submit state
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Canvas
  const [canvasDims, setCanvasDims] = useState({ width: 0, height: 0 })
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const renderTaskRef = useRef<any>(null)

  // ── Load PDF ──
  useEffect(() => {
    if (!assignmentId) return
    setPdfLoading(true)
    const loadPdf = async () => {
      try {
        const url = `/api/patient/form/${assignmentId}/pdf`
        const doc = await pdfjsLib.getDocument(url).promise
        setPdfDoc(doc)
        setNumPages(doc.numPages)
        setPdfLoading(false)
      } catch (err) {
        setPdfError('Failed to load form PDF')
        setPdfLoading(false)
      }
    }
    loadPdf()
  }, [assignmentId])

  // ── Load fields + required indices ──
  useEffect(() => {
    if (!assignmentId) return
    Promise.all([
      fetch(`/api/patient/form/${assignmentId}/fields`).then(r => r.json()),
      fetch(`/api/patient/form/${assignmentId}/required-fields`).then(r => r.json()),
    ]).then(([fieldsData, reqData]) => {
      const fieldsList = Array.isArray(fieldsData) ? fieldsData : (fieldsData.fields || [])
      setFields(fieldsList)
      // Initialize values from existing data
      const initial: Record<number, string> = {}
      fieldsList.forEach((f: FormFieldLike, idx: number) => {
        if (f.value != null && f.value !== '') initial[idx] = f.value as string
      })
      setFieldValues(initial)
      setRequiredFieldIndices(new Set(reqData.patient_required_fields || []))
    }).catch(() => setError('Failed to load form data'))
  }, [assignmentId])

  // ── Render PDF page ──
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel()
      renderTaskRef.current = null
    }
    setRendering(true)
    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(currentPage)
        const viewport = page.getViewport({ scale })
        const canvas = canvasRef.current!
        const ctx = canvas.getContext('2d')
        canvas.width = viewport.width
        canvas.height = viewport.height
        setCanvasDims({ width: viewport.width, height: viewport.height })
        const task = page.render({ canvasContext: ctx, viewport })
        renderTaskRef.current = task
        await task.promise
        renderTaskRef.current = null
        setRendering(false)
      } catch (err) {
        if ((err as Error).name !== 'RenderingCancelledException') setRendering(false)
      }
    }
    renderPage()
  }, [pdfDoc, currentPage, scale])

  // Helper: is this field a signature the patient should sign?
  const isPatientSignature = useCallback((field: FormFieldLike) => {
    const name = (field.name || '').toLowerCase()
    if (field.type === 'signature' && name.includes('patient')) return true
    if (name.includes('patient_signature')) return true
    if (name.includes('patient_authorization_signature')) return true
    return false
  }, [])

  // Helper: is this field editable by the patient?
  const isEditable = useCallback((fieldIndex: number, field: FormFieldLike) => {
    if (isPatientSignature(field)) return true
    return requiredFieldIndices.has(fieldIndex)
  }, [requiredFieldIndices, isPatientSignature])

  // Helper: field type checks
  const isSignatureField = useCallback((field: FormFieldLike) => {
    const name = (field.name || '').toLowerCase()
    if (field.type === 'signature') return true
    return name.includes('signature') && !name.includes('date')
  }, [])

  // Current page fields
  const pageFields = useMemo(() => {
    return fields.filter(f => (f.page || 1) === currentPage)
  }, [fields, currentPage])

  // Validation: all required fields filled + patient signature present
  const validationResult = useMemo(() => {
    const missing: string[] = []
    for (const idx of requiredFieldIndices) {
      const field = fields[idx]
      if (!field) continue
      const val = fieldValues[idx]
      if (!val || !String(val).trim()) {
        missing.push(field.name || field.original_name || `Field ${idx}`)
      }
    }
    // Check patient signature
    let hasSig = false
    fields.forEach((f, idx) => {
      if (isPatientSignature(f) && fieldValues[idx]) hasSig = true
    })
    return { missing, hasSig, canSubmit: missing.length === 0 && hasSig }
  }, [fields, fieldValues, requiredFieldIndices, isPatientSignature])

  // Update field value
  const updateFieldValue = useCallback((fieldIndex: number, value: string) => {
    setFieldValues(prev => ({ ...prev, [fieldIndex]: value }))
  }, [])

  const toggleCheckbox = useCallback((fieldIndex: number) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldIndex]: prev[fieldIndex] ? '' : '\u2713',
    }))
  }, [])

  // Submit: save fields + update status
  const handleSubmit = useCallback(async () => {
    if (!validationResult.canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      // Save patient field values
      const updates: Record<string, string> = {}
      for (const idx of requiredFieldIndices) {
        if (fieldValues[idx] != null) updates[String(idx)] = fieldValues[idx]
      }
      // Include signature fields
      fields.forEach((f, idx) => {
        if (isPatientSignature(f) && fieldValues[idx]) updates[String(idx)] = fieldValues[idx]
      })
      await fetch(`/api/patient/form/${assignmentId}/fields`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field_values: updates }),
      })
      // Update status to pending_provider
      await fetch(`/api/patient/form/${assignmentId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'pending_provider' }),
      })
      setSubmitted(true)
    } catch (err) {
      setError('Failed to submit form. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [assignmentId, validationResult, requiredFieldIndices, fieldValues, fields, isPatientSignature])

  // ── Field overlay styling ──
  const getFieldStyle = (field: FormFieldLike, fieldIndex: number): React.CSSProperties => {
    const [x0, y0, x1, y1] = field.bbox!
    const editable = isEditable(fieldIndex, field)
    const hasValue = !!fieldValues[fieldIndex]
    const isSig = isSignatureField(field)
    const fieldW = field.bbox![2] - field.bbox![0]
    const fieldH = field.bbox![3] - field.bbox![1]
    const isSmallSquare = fieldW > 0 && fieldH > 0 && fieldW < 30 && (fieldW / fieldH) < 3
    const isCheckbox = !isSig && (field.type === 'checkbox' || field.type === 'radio' || field.type === 2 || field.type === 5) && isSmallSquare
    const isFocused = focusedField === fieldIndex
    const isHovered = hoveredField === fieldIndex
    const isRequired = requiredFieldIndices.has(fieldIndex)
    const isPatientSig = isPatientSignature(field)

    return {
      position: 'absolute',
      left: x0 * scale,
      top: y0 * scale,
      width: (x1 - x0) * scale,
      height: (y1 - y0) * scale,
      border: isFocused
        ? '2px solid #182a33'
        : isPatientSig
          ? '2px dashed rgba(230, 140, 50, 0.7)'
          : isRequired && !hasValue
            ? '2px solid rgba(230, 140, 50, 0.7)'
            : isRequired && hasValue
              ? '1.5px solid rgba(108, 138, 97, 0.6)'
              : editable
                ? '1.5px solid rgba(42, 77, 105, 0.4)'
                : hasValue
                  ? '1px solid rgba(42, 77, 105, 0.15)'
                  : '1px solid rgba(42, 77, 105, 0.08)',
      backgroundColor: isFocused
        ? 'rgba(255, 255, 255, 0.95)'
        : isPatientSig
          ? 'rgba(245, 165, 50, 0.12)'
          : isRequired && !hasValue
            ? 'rgba(245, 165, 50, 0.15)'
            : isRequired && hasValue
              ? 'rgba(108, 138, 97, 0.08)'
              : editable && isHovered
                ? 'rgba(255, 255, 255, 0.85)'
                : editable
                  ? 'rgba(255, 255, 255, 0.6)'
                  : hasValue
                    ? 'rgba(255, 255, 255, 0.4)'
                    : 'rgba(255, 255, 255, 0.2)',
      borderRadius: isCheckbox ? '2px' : '3px',
      cursor: editable ? (isCheckbox ? 'pointer' : 'text') : 'default',
      overflow: 'hidden',
      zIndex: isFocused ? 20 : isHovered ? 10 : 1,
      transition: 'background-color 0.15s, border-color 0.15s',
      boxShadow: isFocused ? '0 0 0 3px rgba(42, 77, 105, 0.12)' : 'none',
    }
  }

  // ── Success screen ──
  if (submitted) {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: '#f5f0eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', backgroundColor: '#eef4f2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px', fontSize: 32, color: '#6b9c8e',
          }}>&#10003;</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#182a33', marginBottom: 8 }}>
            Form Submitted
          </h2>
          <p style={{ fontSize: 16, color: '#5b6e7c', marginBottom: 12 }}>
            Your signed form has been submitted successfully.
          </p>
          <p style={{ fontSize: 14, color: '#7e8c96', marginBottom: 24, lineHeight: 1.6 }}>
            It is now being faxed to your insurance company, pharmacy, and family physician.
            Your provider will be notified and you'll receive updates on the approval status.
          </p>
          <button onClick={() => navigate('/dashboard')} style={{
            padding: '12px 32px', fontSize: 14, fontWeight: 600,
            backgroundColor: '#182a33', color: '#fff', border: 'none',
            borderRadius: 8, cursor: 'pointer',
          }}>
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // ── Loading ──
  if (pdfLoading) {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: '#f5f0eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ margin: '0 auto 16px', display: 'inline-flex' }}>
            <ParseoSpinner size={40} />
          </div>
          <p style={{ fontSize: 16, color: '#5b6e7c' }}>Loading your form...</p>
        </div>
      </div>
    )
  }

  if (pdfError) {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: '#f5f0eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <p style={{ fontSize: 16, color: '#c25450' }}>{pdfError}</p>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      backgroundColor: '#f5f0eb', fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#182a33', color: 'white', padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ParseoLogo size={22} />
          <div style={{ fontSize: 20, fontWeight: 700 }}>Parseo Health</div>
          <span style={{ opacity: 0.7, fontSize: 13 }}>Complete Your Form</span>
        </div>
        <button onClick={() => navigate('/dashboard')} style={{
          color: 'white', background: 'none', border: '1px solid rgba(255,255,255,0.3)',
          padding: '6px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
        }}>
          Back
        </button>
      </header>

      {/* Main content */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* PDF viewer */}
        <div ref={containerRef} style={{
          flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column',
          alignItems: 'center', padding: '16px 8px', backgroundColor: '#e8e2d8',
        }}>
          {/* Page controls */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
            padding: '8px 16px', backgroundColor: '#fff', borderRadius: 8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
              style={{ padding: '4px 12px', border: '1px solid #d8c9b6', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', opacity: currentPage <= 1 ? 0.4 : 1 }}>
              Prev
            </button>
            <span style={{ fontSize: 14, color: '#182a33' }}>
              Page {currentPage} of {numPages}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))} disabled={currentPage >= numPages}
              style={{ padding: '4px 12px', border: '1px solid #d8c9b6', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', opacity: currentPage >= numPages ? 0.4 : 1 }}>
              Next
            </button>
            <span style={{ fontSize: 12, color: '#5b6e7c', marginLeft: 8 }}>
              Zoom:
            </span>
            <button onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
              style={{ padding: '2px 8px', border: '1px solid #d8c9b6', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer' }}>-</button>
            <span style={{ fontSize: 12, color: '#182a33', minWidth: 36, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.min(2.5, s + 0.1))}
              style={{ padding: '2px 8px', border: '1px solid #d8c9b6', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer' }}>+</button>
          </div>

          {/* Canvas + overlays */}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <canvas ref={canvasRef} style={{
              borderRadius: 4, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff',
            }} />

            {/* Field overlays */}
            {canvasDims.width > 0 && pageFields.map((field, idx) => {
              if (!field.bbox || field.bbox.length < 4) return null
              const fieldIndex = fields.indexOf(field)
              const editable = isEditable(fieldIndex, field)
              const isSig = isSignatureField(field)
              const isPatientSig = isPatientSignature(field)
              const bboxW = field.bbox[2] - field.bbox[0]
              const bboxH = field.bbox[3] - field.bbox[1]
              const isSmallSquare = bboxW > 0 && bboxH > 0 && bboxW < 30 && (bboxW / bboxH) < 3
              const isCheckbox = !isSig && (field.type === 'checkbox' || field.type === 'radio' || field.type === 2 || field.type === 5) && isSmallSquare
              const fieldHeight = bboxH * scale
              const fieldWidth = bboxW * scale
              const value = fieldValues[fieldIndex] || ''
              const fieldName = field.displayName || field.name || field.original_name || ''
              // Wrap eligibility keys on the bbox in PDF POINTS, mirroring the
              // burned-PDF renderer's 22pt rule (and physician-ui FieldOverlay)
              // — a rendered-px gate drifts with zoom and left mid-height boxes
              // clipping on screen while the sent PDF wraps them
              // (imp_fa89f3ac). Format-shaped fields (dates, phones) stay
              // single-line inputs.
              const isMultiLine = field.type === 'textarea' || field.type === 'large_text' ||
                (!inferInputFormat(field) && wrapsInSentPdf(bboxH))

              return (
                <div
                  key={field.id || field.linkId || `field-${idx}`}
                  style={getFieldStyle(field, fieldIndex)}
                  onMouseEnter={() => setHoveredField(fieldIndex)}
                  onMouseLeave={() => setHoveredField(null)}
                  onClick={editable && isCheckbox ? () => toggleCheckbox(fieldIndex) : undefined}
                  title={editable ? fieldName : `${fieldName} (filled by your provider)`}
                >
                  {isPatientSig && !value ? (
                    <button
                      onClick={() => {
                        // For now, prompt for typed signature
                        const sig = window.prompt('Type your full name to sign:')
                        if (sig) updateFieldValue(fieldIndex, sig)
                      }}
                      style={{
                        width: '100%', height: '100%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', gap: 4,
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#e8943a', fontSize: Math.min(fieldHeight * 0.35, 14), fontWeight: 500,
                      }}
                    >
                      Click to Sign
                    </button>
                  ) : isPatientSig && value ? (
                    <div style={{
                      width: '100%', height: '100%', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: Math.min(fieldHeight * 0.4, 16),
                      fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                      color: '#182a33', fontWeight: 500,
                      cursor: 'pointer',
                    }} onClick={() => {
                      if (window.confirm('Clear your signature?')) updateFieldValue(fieldIndex, '')
                    }}>
                      {value}
                    </div>
                  ) : isSig ? (
                    // Non-patient signature — read-only. Render base64 image
                    // signatures as <img>, typed-name signatures in a script font.
                    // Mirrors physician-ui-app/src/components/FormViewer/FieldOverlay.jsx.
                    value && typeof value === 'string' && value.startsWith('data:image') ? (
                      <div style={{
                        width: '100%', height: '100%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <img
                          src={value}
                          alt="Signature"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      </div>
                    ) : value && typeof value === 'string' && value.trim().length > 0 ? (
                      <div style={{
                        width: '100%', height: '100%', display: 'flex',
                        alignItems: 'center', justifyContent: 'flex-start',
                        padding: '2px 6px', boxSizing: 'border-box',
                      }}>
                        <span style={{
                          fontFamily: "'Sacramento', 'Brush Script MT', cursive",
                          fontSize: Math.min(fieldHeight * 0.95, 22),
                          color: '#182a33', lineHeight: 1,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{value}</span>
                      </div>
                    ) : (
                      <div style={{ width: '100%', height: '100%' }} />
                    )
                  ) : isCheckbox ? (
                    <div style={{
                      width: '100%', height: '100%', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      // Mirror the physician UI (FieldOverlay.jsx): filled = solid
                      // green with white check so a ticked box reads at a glance
                      // on the form's tiny native checkbox squares. Unchecked stays
                      // transparent. For editable patient checkboxes we use a softer
                      // amber fill to distinguish them from physician-set ones.
                      backgroundColor: value
                        ? (editable ? '#c9772a' : '#4a6844')
                        : 'transparent',
                      color: value ? '#ffffff' : 'transparent',
                      borderRadius: '2px',
                      fontSize: Math.max(9, Math.min(Math.min(fieldHeight, (field.bbox[2] - field.bbox[0]) * scale) * 0.9, 16)),
                      fontWeight: 800, lineHeight: 1, userSelect: 'none',
                      cursor: editable ? 'pointer' : 'default',
                    }}>
                      {value ? '\u2713' : ''}
                    </div>
                  ) : isMultiLine ? (
                    <textarea
                      value={value}
                      readOnly={!editable}
                      placeholder={editable ? fieldName : ''}
                      title={value || undefined}
                      onChange={editable ? (e) => updateFieldValue(fieldIndex, e.target.value) : undefined}
                      onFocus={editable ? () => setFocusedField(fieldIndex) : undefined}
                      onBlur={editable ? () => setFocusedField(null) : undefined}
                      style={{
                        width: '100%', height: '100%', border: 'none', outline: 'none',
                        background: 'transparent', padding: '6px 8px', margin: 0,
                        borderRadius: 0,
                        // Shrink-to-fit so a long value wraps inside the box —
                        // matching the sent PDF — instead of hiding behind a
                        // scrollbar. padX/padY mirror the CSS padding ('6px
                        // 8px') plus a safety margin; lineHeight matches the
                        // fit helper's LINE_H (1.3).
                        fontSize: fitTextareaFontSize(value, fieldWidth, fieldHeight, {
                          maxPx: Math.max(12, Math.min(fieldHeight * 0.12, 14)), minPx: 6,
                          padX: 16, padY: 14,
                        }),
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        color: editable ? '#182a33' : '#5b6e7c',
                        fontWeight: value ? 500 : 400,
                        lineHeight: 1.3, boxShadow: 'none', resize: 'none',
                        overflow: 'auto', whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                        cursor: editable ? 'text' : 'default', boxSizing: 'border-box',
                      }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={value}
                      readOnly={!editable}
                      placeholder={editable ? fieldName : ''}
                      title={value || undefined}
                      onChange={editable ? (e) => updateFieldValue(fieldIndex, e.target.value) : undefined}
                      onFocus={editable ? () => setFocusedField(fieldIndex) : undefined}
                      onBlur={editable ? () => setFocusedField(null) : undefined}
                      style={{
                        width: '100%', height: '100%', border: 'none', outline: 'none',
                        background: 'transparent', padding: '1px 3px', margin: 0,
                        // Shrink on width so a long value stays inside the box
                        // (full text on hover) rather than clipping mid-word.
                        borderRadius: 0,
                        fontSize: fitFontSize(value, fieldWidth, {
                          maxPx: Math.min(fieldHeight * 0.65, 13), minPx: 7, padX: 6,
                        }),
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        color: editable ? '#182a33' : '#5b6e7c',
                        fontWeight: value ? 500 : 400,
                        cursor: editable ? 'text' : 'default',
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Right panel — instructions + submit */}
        <div style={{
          width: 300, flexShrink: 0, backgroundColor: '#fff', borderLeft: '1px solid #e6dccb',
          padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#182a33', margin: 0 }}>
            Complete Your Form
          </h2>
          <p style={{ fontSize: 14, color: '#5b6e7c', lineHeight: 1.5, margin: 0 }}>
            Your provider has filled out most of this form. Please review and complete the
            highlighted fields, then sign at the bottom.
          </p>

          {/* Required fields checklist */}
          {requiredFieldIndices.size > 0 && (
            <div style={{ backgroundColor: '#fef9ee', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#c9772a', marginBottom: 8 }}>
                Fields you need to fill ({validationResult.missing.length} remaining)
              </div>
              {[...requiredFieldIndices].map(idx => {
                const field = fields[idx]
                if (!field) return null
                const name = field.displayName || field.name || field.original_name || `Field ${idx}`
                const filled = !!(fieldValues[idx] && String(fieldValues[idx]).trim())
                return (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontSize: 13, color: filled ? '#6b9c8e' : '#c9772a',
                    padding: '3px 0',
                  }}>
                    <span>{filled ? '\u2713' : '\u25cb'}</span>
                    <span>
                      {name.replace(/_/g, ' ')}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Signature status */}
          <div style={{
            backgroundColor: validationResult.hasSig ? '#eef4f2' : '#fdf4ea',
            borderRadius: 8, padding: 12,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: validationResult.hasSig ? '#6b9c8e' : '#e65100' }}>
              {validationResult.hasSig ? '\u2713 Signed' : 'Signature required'}
            </div>
            {!validationResult.hasSig && (
              <p style={{ fontSize: 12, color: '#5b6e7c', margin: '4px 0 0' }}>
                Scroll to find the patient signature field and click to sign.
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div style={{ padding: '8px 12px', backgroundColor: '#faeeed', borderRadius: 8, fontSize: 13, color: '#c25450' }}>
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={!validationResult.canSubmit || submitting}
            style={{
              padding: '14px 0', fontSize: 16, fontWeight: 600, borderRadius: 8,
              border: 'none', cursor: validationResult.canSubmit ? 'pointer' : 'default',
              backgroundColor: validationResult.canSubmit ? '#182a33' : '#d8c9b6',
              color: validationResult.canSubmit ? 'white' : '#5b6e7c',
              transition: 'background-color 0.2s',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? (<span style={{display:'inline-flex',alignItems:'center',gap:8}}><ParseoSpinner size={16} color="#ffffff"/> Submitting...</span>) : validationResult.canSubmit ? 'Submit Form' : 'Complete all fields to submit'}
          </button>

          <p style={{ fontSize: 11, color: '#7e8c96', textAlign: 'center', margin: 0 }}>
            By submitting, you confirm the information is accurate.
          </p>
        </div>
      </div>
    </div>
  )
}
